import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Shuffle, RotateCw } from "lucide-react";
import { cleanText } from "@/components/Markdown";

const plain = (s) => cleanText(s).replace(/[*_`>#]/g, "").replace(/\s+/g, " ").trim();

// Built from the lesson notes in the browser — no Gemini requests.
export function cardsFromNotes(md = "") {
  const cards = [];
  const sections = md.split(/\n(?=## )/);
  for (const sec of sections) {
    const title = (sec.match(/^##\s+(.+)/) || [])[1]?.trim().toLowerCase() || "";
    if (title.startsWith("key concepts")) {
      sec.split(/\n(?=### )/).slice(1).forEach((c) => {
        const [head, ...body] = c.split("\n");
        const back = plain(body.map((l) => l.replace(/^\s*[-*]\s+/, "• ")).join(" ")).slice(0, 320);
        if (back) cards.push({ front: plain(head.replace(/^###/, "")), back, kind: "Concept" });
      });
    } else if (title.startsWith("formula sheet")) {
      sec.split("\n").filter((l) => /^\|/.test(l) && !/^\|\s*-/.test(l)).slice(1).forEach((row) => {
        const [f, meaning, units] = row.split("|").slice(1, -1).map(plain);
        if (f && meaning) cards.push({ front: meaning, back: `${f}${units ? `  (units: ${units})` : ""}`, kind: "Formula" });
      });
    } else if (title.startsWith("definitions") || title.startsWith("formulas")) {
      sec.split("\n").filter((l) => /^\s*[-*]\s/.test(l)).forEach((l) => {
        const m = plain(l.replace(/^\s*[-*]\s/, "")).split(/\s*[:–—]\s+/);
        if (m.length > 1 && m[0].length < 80) cards.push({ front: m[0], back: m.slice(1).join(": "), kind: "Definition" });
      });
    } else if (title.startsWith("common mistakes")) {
      sec.split("\n").filter((l) => /^\s*[-*]\s/.test(l)).slice(0, 4).forEach((l, i) => {
        cards.push({ front: `Common mistake #${i + 1} — what should you avoid?`, back: plain(l.replace(/^\s*[-*]\s/, "")), kind: "Mistake" });
      });
    }
  }
  return cards.slice(0, 40);
}

const KIND_COLOR = { Concept: "#22D3EE", Formula: "#F472B6", Definition: "#A78BFA", Mistake: "#F59E0B" };

export default function Flashcards({ notes }) {
  const base = useMemo(() => cardsFromNotes(notes), [notes]);
  const [order, setOrder] = useState(null);
  const cards = order || base;
  const [i, setI] = useState(0);
  const [flip, setFlip] = useState(false);
  if (!cards.length) return <div className="text-sm text-slate-500 p-6" data-testid="flashcards-empty">No flashcards yet for this lesson. They appear once notes are ready.</div>;
  const c = cards[Math.min(i, cards.length - 1)];
  const go = (d) => { setFlip(false); setI((x) => (x + d + cards.length) % cards.length); };
  return (
    <div className="max-w-xl mx-auto py-4" data-testid="flashcards">
      <div className="flex items-center justify-between text-xs text-slate-500 mb-3">
        <span data-testid="flashcard-counter">Card {i + 1} of {cards.length}</span>
        <button data-testid="flashcard-shuffle" onClick={() => { setOrder([...base].sort(() => Math.random() - 0.5)); setI(0); setFlip(false); }} className="inline-flex items-center gap-1 hover:text-slate-200"><Shuffle className="w-3.5 h-3.5" /> Shuffle</button>
      </div>
      <button data-testid="flashcard" onClick={() => setFlip((f) => !f)} className="w-full [perspective:1200px]">
        <div className="relative h-60 transition-transform duration-500 [transform-style:preserve-3d]" style={{ transform: flip ? "rotateY(180deg)" : "none" }}>
          <div className="absolute inset-0 rounded-2xl border border-[#1E293B] bg-[#111726] p-6 flex flex-col [backface-visibility:hidden]">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: KIND_COLOR[c.kind] }}>{c.kind}</span>
            <div className="flex-1 flex items-center justify-center text-center text-lg font-display font-bold text-slate-100">{c.front}</div>
            <span className="text-[11px] text-slate-600 inline-flex items-center gap-1 justify-center"><RotateCw className="w-3 h-3" /> Tap to flip</span>
          </div>
          <div className="absolute inset-0 rounded-2xl border border-emerald-500/40 bg-[#0E1420] p-6 flex items-center justify-center text-center text-sm leading-relaxed text-slate-200 overflow-y-auto [backface-visibility:hidden] [transform:rotateY(180deg)]" data-testid="flashcard-back">
            {c.back}
          </div>
        </div>
      </button>
      <div className="flex justify-center gap-3 mt-4">
        <button data-testid="flashcard-prev" onClick={() => go(-1)} className="w-11 h-11 rounded-full bg-[#111726] border border-[#1E293B] flex items-center justify-center hover:border-emerald-500/50"><ChevronLeft className="w-5 h-5" /></button>
        <button data-testid="flashcard-next" onClick={() => go(1)} className="w-11 h-11 rounded-full bg-emerald-400 text-slate-950 flex items-center justify-center hover:bg-emerald-300"><ChevronRight className="w-5 h-5" /></button>
      </div>
    </div>
  );
}
